/* Zareen Jewels — cart page: in-place quantity / remove (Cart AJAX API + section rendering),
   gift note autosave, delivery estimate, sticky checkout bar, header offset, live refresh
   when something is added from a rail on this page. */
(() => {
  const html = document.documentElement;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const init = (root) => {
    if (!root || root.dataset.zjqReady) return;
    root.dataset.zjqReady = '1';
    const sectionId = root.dataset.section;
    const live = root.querySelector('[data-zjq-live]');
    const say = (msg) => { if (live) { live.textContent = ''; setTimeout(() => { live.textContent = msg; }, 60); } };

    /* ---------- render helpers ---------- */
    const bubbleUpdate = (sections) => {
      const bubble = document.getElementById('cart-icon-bubble');
      const markup = sections && sections['cart-icon-bubble'];
      if (!bubble || !markup) return;
      const doc = new DOMParser().parseFromString(markup, 'text/html');
      const inner = doc.querySelector('.shopify-section');
      bubble.innerHTML = inner ? inner.innerHTML : doc.body.innerHTML;
    };
    const applySection = (markup) => {
      if (!markup) return;
      const doc = new DOMParser().parseFromString(markup, 'text/html');
      const fresh = doc.getElementById(root.id);
      if (!fresh) return;
      const noteOpen = root.querySelector('.zjq-note')?.open;
      const active = document.activeElement;
      const activeKey = active && active.closest('[data-zjq-line]') ? active.closest('[data-zjq-line]').dataset.key : null;
      const activeStep = active && active.dataset ? active.dataset.zjqStep : null;
      root.classList.add('is-rendered');
      ['[data-zjq-body]', '[data-zjq-count]', '.zjq-band__sub'].forEach((sel) => {
        const a = root.querySelector(sel), b = fresh.querySelector(sel);
        if (a && b) a.innerHTML = b.innerHTML;
        else if (a && !b) a.remove();
        else if (!a && b && sel === '.zjq-band__sub') root.querySelector('.zjq-band__inner').appendChild(b);
      });
      root.classList.toggle('is-empty', fresh.classList.contains('is-empty'));
      root.dataset.count = fresh.dataset.count;
      const note = root.querySelector('.zjq-note');
      if (note && noteOpen) note.open = true;
      if (activeKey) {
        const line = root.querySelector(`[data-zjq-line][data-key="${CSS.escape(activeKey)}"]`);
        const target = line && (activeStep ? line.querySelector(`[data-zjq-step="${activeStep}"]`) : line.querySelector('[data-zjq-input]'));
        if (target) target.focus({ preventScroll: true });
      }
      if (fresh.dataset.count === '0') {
        document.querySelectorAll('.zjr2').forEach((r) => { const sec = r.closest('.shopify-section'); (sec || r).remove(); });
        releaseBar();
      }
      wire();
      document.dispatchEvent(new CustomEvent('zj:rendered'));
    };

    /* ---------- queue so requests never race ---------- */
    let queue = Promise.resolve();
    const enqueue = (fn) => { queue = queue.then(fn, fn); return queue; };

    const change = (key, quantity) => enqueue(async () => {
      const line = root.querySelector(`[data-zjq-line][data-key="${CSS.escape(key)}"]`);
      if (line) line.classList.add('is-busy');
      try {
        const res = await fetch(`${(window.routes && window.routes.cart_change_url) || '/cart/change'}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity, sections: [sectionId, 'cart-icon-bubble'], sections_url: window.location.pathname }),
        });
        const data = await res.json();
        if (!res.ok || data.status) {
          if (line) {
            line.classList.remove('is-busy', 'is-removing');
            const err = line.querySelector('[data-zjq-err]');
            if (err) { err.textContent = data.description || data.message || 'Something went wrong. Please try again.'; err.hidden = false; }
            const input = line.querySelector('[data-zjq-input]');
            if (input) input.value = input.defaultValue;
          }
          return;
        }
        applySection(data.sections && data.sections[sectionId]);
        bubbleUpdate(data.sections);
        say(quantity === 0 ? 'Removed from your bag.' : `Bag updated. ${data.item_count} ${data.item_count === 1 ? 'piece' : 'pieces'}.`);
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'zj-cart', cartData: data });
      } catch (e) {
        window.location.reload();
      }
    });

    const removeLine = (line) => {
      if (!line || line.classList.contains('is-removing')) return;
      const key = line.dataset.key;
      if (reduce) { change(key, 0); return; }
      line.classList.add('is-removing');
      setTimeout(() => {
        line.style.height = `${line.offsetHeight}px`;
        requestAnimationFrame(() => { line.classList.add('is-collapsing'); line.style.height = '0px'; });
      }, 260);
      change(key, 0);
    };

    /* ---------- events (delegated so they survive re-renders) ---------- */
    const timers = new Map();
    root.addEventListener('click', (e) => {
      const x = e.target.closest('[data-zjq-remove]');
      if (x) { e.preventDefault(); removeLine(x.closest('[data-zjq-line]')); return; }
      const step = e.target.closest('[data-zjq-step]');
      if (step) {
        const line = step.closest('[data-zjq-line]');
        const input = line.querySelector('[data-zjq-input]');
        const max = input.max ? Number(input.max) : Infinity;
        const next = Math.max(0, Math.min(max, (Number(input.value) || 0) + Number(step.dataset.zjqStep)));
        if (next === 0) { removeLine(line); return; }
        input.value = next;
        const key = line.dataset.key;
        clearTimeout(timers.get(key));
        timers.set(key, setTimeout(() => { if (String(next) !== input.defaultValue) change(key, Number(input.value)); }, 380));
        return;
      }
      const checkout = e.target.closest('[data-zjq-checkout]');
      if (checkout) checkout.classList.add('is-loading');
    });
    root.addEventListener('change', (e) => {
      const input = e.target.closest('[data-zjq-input]');
      if (!input) return;
      const line = input.closest('[data-zjq-line]');
      let q = Math.max(0, Math.floor(Number(input.value) || 0));
      if (input.max) q = Math.min(q, Number(input.max));
      if (q === 0) { removeLine(line); return; }
      input.value = q;
      if (String(q) !== input.defaultValue) change(line.dataset.key, q);
    });
    root.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && e.target.matches('[data-zjq-input]')) { e.preventDefault(); e.target.blur(); }
    });

    /* gift note: autosave */
    let noteTimer = 0;
    root.addEventListener('input', (e) => {
      const field = e.target.closest('[data-zjq-note]');
      if (!field) return;
      const status = root.querySelector('[data-zjq-note-status]');
      if (status) status.textContent = 'Saving…';
      clearTimeout(noteTimer);
      noteTimer = setTimeout(async () => {
        try {
          await fetch(`${(window.routes && window.routes.cart_update_url) || '/cart/update'}.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ note: field.value }),
          });
          if (status) status.textContent = field.value.trim() ? '✓ Saved to your order' : '';
        } catch (err) {
          if (status) status.textContent = 'Could not save, it will be sent with checkout.';
        }
      }, 600);
    });

    /* ---------- header offset for the sticky summary ---------- */
    const header = document.querySelector('.zj-header-section');
    if (header) {
      let raf = 0;
      const sync = () => { raf = 0; root.style.setProperty('--zjq-top', `${Math.max(0, Math.round(header.getBoundingClientRect().bottom))}px`); };
      window.addEventListener('scroll', () => { if (!raf) raf = requestAnimationFrame(sync); }, { passive: true });
      new MutationObserver(() => setTimeout(sync, 380)).observe(header, { attributes: true, attributeFilter: ['class'] });
      sync();
    }

    /* ---------- sticky bar + delivery estimate (re-wired after each render) ---------- */
    let io = null;
    const releaseBar = () => { html.style.removeProperty('--zj-float-lift'); };
    const wire = () => {
      if (io) { io.disconnect(); io = null; }
      const bar = root.querySelector('[data-zjq-bar]');
      const btn = root.querySelector('[data-zjq-checkout]');
      if (bar && btn && 'IntersectionObserver' in window) {
        const mq = window.matchMedia('(max-width: 989px)');
        io = new IntersectionObserver(([entry]) => {
          const show = mq.matches && !entry.isIntersecting && entry.boundingClientRect.top > 0;
          bar.classList.toggle('is-shown', show);
          bar.setAttribute('aria-hidden', String(!show));
          const b = bar.querySelector('button');
          if (b) b.tabIndex = show ? 0 : -1;
          if (show) html.style.setProperty('--zj-float-lift', `${bar.offsetHeight}px`); else releaseBar();
        });
        io.observe(btn);
      }
      const eta = root.querySelector('[data-zjq-eta]');
      if (eta) {
        const add = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return d; };
        const a = add(Number(eta.dataset.min) || 3), b = add(Number(eta.dataset.max) || 6);
        const day = (d) => d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric' });
        const month = (d) => d.toLocaleDateString('en-GB', { month: 'short' });
        const text = a.getMonth() === b.getMonth() ? `${day(a)} – ${day(b)} ${month(b)}` : `${day(a)} ${month(a)} – ${day(b)} ${month(b)}`;
        eta.querySelector('[data-zjq-eta-range]').textContent = text;
        eta.hidden = false;
      }
    };
    wire();

    /* ---------- something added elsewhere on this page (e.g. a rail) → refresh ---------- */
    const refresh = async () => {
      try {
        const res = await fetch(`${window.location.pathname}?section_id=${encodeURIComponent(sectionId)}`);
        applySection(await res.text());
      } catch (e) {}
    };
    document.addEventListener('zj:cart-added', () => enqueue(refresh));
  };

  const boot = (scope) => scope.querySelectorAll('[data-zjq]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot(document));
  else boot(document);
  document.addEventListener('shopify:section:load', (e) => boot(e.target));
})();
